import React, { createRef, FC, useState } from 'react';
import { useSelector } from 'react-redux';

// Shared UI Library Components
import { DiscardModal, SpinElement, TabsElement } from '@ppmcore/seven-ppm-core-shared-components-react';

// Styles
import './session-settings.scss';

// Components
import { PayPerMinuteTab, TPayPerMinuteTabRef } from '../pay-per-minute-tab/pay-per-minute-tab';
import { CorrespondenceTab, TCorrespondenceTabRef } from '../correspondence-tab/correspondence-tab';
import { PayPerSessionTab, TPayPerSessionTabRef } from '../pay-per-session-tab/pay-per-session-tab';

// Store entities
import { getSessionSettingsState } from '../../../../store/session-settings/session-settings.selectors';

const defaultActiveKey = 'pay-per-minute';

type TTabKeys = 'pay-per-minute' | 'pay-per-session' | 'correspondence';

type TCounsellorAttributesProps = {};

export const SessionSettings: FC<TCounsellorAttributesProps> = (
  {}: TCounsellorAttributesProps
) => {
  const payPerMinuteRef = createRef<TPayPerMinuteTabRef>();
  const payPerSessionRef = createRef<TPayPerSessionTabRef>();
  const correspondenceRef = createRef<TCorrespondenceTabRef>();

  const [discardOpen, setDiscardOpen] = useState<boolean>(false);
  const [activeKey, setActiveKey] = useState<TTabKeys>(defaultActiveKey);
  const [nextKey, setNextKey] = useState<TTabKeys | null>(null);

  const { sessions, appointmentsSettings, booked, correspondence, loading } = useSelector(getSessionSettingsState);

  const toggleDiscardModal = (): void => {
    setDiscardOpen(!discardOpen);
  }

  const resetRefState = (): void => {
    if (activeKey === 'pay-per-minute') {
      payPerMinuteRef.current?.resetState();
      return;
    }
    if (activeKey === 'pay-per-session') {
      payPerSessionRef.current?.resetState();
      return;
    }
    if (activeKey === 'correspondence') {
      correspondenceRef.current?.resetState();
      return;
    }
  }

  const onDiscardChangesHandler = (state: boolean): void => {
    if (state) {
      toggleDiscardModal();
      resetRefState();
      setActiveKey(nextKey ?? defaultActiveKey);
      setNextKey(null);
      return;
    }
    toggleDiscardModal();
  }

  const onTabClickHandler = (key: string, event: any) => {
    event.preventDefault();
    setNextKey(key as TTabKeys);

    if (payPerMinuteRef.current?.isHasChanges() || payPerSessionRef.current?.isHasChanges() || correspondenceRef.current?.isHasChanges()) {
      toggleDiscardModal();
      return;
    }

    setActiveKey(key as TTabKeys);
  }

  return (
    <div className="session-settings">

      <DiscardModal
        open={ discardOpen }
        onCancel={ onDiscardChangesHandler }
        title={ 'Are you sure you want to discard changes?' }
      />

      <div className="session-settings--header">
        <div className="header-title">
          Consultations Settings
        </div>
      </div>

      <div className={ `session-settings--loader session-settings--loader-${ loading ? 'show' : 'hide' }` }>
        <SpinElement fullHeight={ true }/>
      </div>

      <div className="session-settings--body">
        <TabsElement activeKey={ activeKey } onTabClick={ onTabClickHandler } items={ [
          {
            key: 'pay-per-minute',
            label: 'Minute-Based Calls',
            children: <PayPerMinuteTab ref={ payPerMinuteRef } booked={ booked }/>,
          },
          {
            key: 'pay-per-session',
            label: 'Booking Appointments',
            children: <PayPerSessionTab ref={ payPerSessionRef } sessions={ sessions } appointmentsSettings={ appointmentsSettings } />,
          },
          {
            key: 'correspondence',
            label: 'Text-Based Requests',
            children: <CorrespondenceTab ref={ correspondenceRef } correspondence={ correspondence } />,
          },
        ] }/>
      </div>
    </div>
  );
}
